<template>
	<div>
		<div class="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
			<WSubTitle text="Data validity heatmap" />
			<span class="text-sm text-color-secondary">NEEi,act hours · green = valid</span>
		</div>
		<div v-if="!cells.length" class="dash-empty">
			<p>No hourly validity data for this period.</p>
		</div>
		<div v-else class="heatmap-wrap">
			<div class="heatmap-hours">
				<span class="day-label" />
				<span v-for="h in hours" :key="h" class="hour-label">{{ h }}</span>
			</div>
			<div
				v-for="day in days"
				:key="day"
				class="heatmap-row"
			>
				<span class="day-label">{{ day.slice(5) }}</span>
				<span
					v-for="h in hours"
					:key="day + '-' + h"
					class="cell"
					:class="cellClass(day, h)"
					:title="tooltip(day, h)"
				/>
			</div>
			<div class="legend mt-2 text-sm text-color-secondary">
				<span class="cell valid legend-swatch" /> valid
				<span class="cell invalid legend-swatch ml-2" /> invalid
				<span class="cell missing legend-swatch ml-2" /> missing
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, PropType } from 'vue'
	import { DashboardValidityCell } from '@/brokers/DashboardBroker'

	export default defineComponent({
		name: 'DashValidityHeatmap',
		props: {
			cells: {
				type: Array as PropType<DashboardValidityCell[]>,
				default: () => [],
			},
		},
		computed: {
			hours(): number[] {
				return Array.from({ length: 24 }, (_, i) => i)
			},
			days(): string[] {
				const set = new Set((this.cells || []).map((c) => c.day))
				return Array.from(set).sort()
			},
			lookup(): Record<string, boolean> {
				const map: Record<string, boolean> = {}
				for (const c of this.cells || []) {
					map[`${c.day}|${c.hour}`] = c.is_valid
				}
				return map
			},
		},
		methods: {
			cellClass(day: string, hour: number): string {
				const key = `${day}|${hour}`
				if (!(key in this.lookup)) return 'missing'
				return this.lookup[key] ? 'valid' : 'invalid'
			},
			tooltip(day: string, hour: number): string {
				const key = `${day}|${hour}`
				if (!(key in this.lookup)) return `${day} ${String(hour).padStart(2, '0')}:00 — missing`
				return `${day} ${String(hour).padStart(2, '0')}:00 — ${this.lookup[key] ? 'valid' : 'invalid'}`
			},
		},
	})
</script>

<style scoped>
	.heatmap-wrap {
		overflow-x: auto;
	}
	.heatmap-hours {
		display: grid;
		grid-template-columns: 3.2rem repeat(24, 14px);
		gap: 2px;
		margin-bottom: 2px;
		font-size: 0.65rem;
		color: var(--text-color-secondary);
	}
	.hour-label {
		text-align: center;
	}
	.heatmap-hours .hour-label:first-child {
		/* spacer aligns with day label — unused first cell via empty first */
	}
	.heatmap-row {
		display: grid;
		grid-template-columns: 3.2rem repeat(24, 14px);
		gap: 2px;
		margin-bottom: 2px;
		align-items: center;
	}
	.day-label {
		font-size: 0.7rem;
		color: var(--text-color-secondary);
	}
	.cell {
		width: 14px;
		height: 14px;
		border-radius: 2px;
		display: inline-block;
	}
	.cell.valid {
		background: #22c55e;
	}
	.cell.invalid {
		background: #f97316;
	}
	.cell.missing {
		background: var(--surface-border);
	}
	.legend-swatch {
		vertical-align: middle;
		margin-right: 0.25rem;
	}
	.dash-empty {
		padding: 1.5rem;
		text-align: center;
		color: var(--text-color-secondary);
		border: 1px dashed var(--surface-border);
		border-radius: 6px;
	}
</style>
